import { useEffect, useState, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import {
  authHeadersForMultipart,
  authHeadersJson,
  getStoredAccessToken,
} from '../api/http';
import { Message } from '../types/message';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export type ChatChannelProvider = 'meta' | 'whatsapp_web';

function buildChatQuery(companyId: number, provider: ChatChannelProvider): string {
  const p = provider === 'whatsapp_web' ? 'whatsapp_web' : 'meta';
  return `companyId=${encodeURIComponent(String(companyId))}&provider=${encodeURIComponent(p)}`;
}

const MESSAGE_STATUSES = ['sent', 'delivered', 'read', 'failed'] as const;

function toMessageStatus(value: string): Message['status'] {
  return MESSAGE_STATUSES.includes(value as Message['status'])
    ? (value as Message['status'])
    : 'sent';
}

function normalizeFromMe(raw: Message): boolean {
  const extended = raw as Message & { from_me?: unknown };
  const v: unknown = raw.fromMe ?? extended.from_me;
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  return Boolean(v);
}

function normalizeMessagesCredentialId(raw: Message): number | null {
  const extended = raw as Message & { messages_credential_id?: unknown };
  const v = raw.messagesCredentialId ?? extended.messages_credential_id;
  if (v === null || v === undefined) {
    return null;
  }
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function credentialMatchesFilter(
  msg: Message,
  channelProvider: ChatChannelProvider,
  activeCredentialId: number | null,
): boolean {
  const mid = msg.messagesCredentialId;
  if (channelProvider === 'whatsapp_web') {
    return activeCredentialId != null && mid === activeCredentialId;
  }
  if (activeCredentialId == null) {
    return true;
  }
  return mid === null || mid === activeCredentialId;
}

function normalizeRevokedAt(raw: Message): Date | null {
  const ext = raw as Message & { revoked_at?: unknown };
  const v = raw.revokedAt ?? ext.revoked_at;
  if (v == null) {
    return null;
  }
  if (v instanceof Date) {
    return v;
  }
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeChatMessage(raw: Message): Message {
  return {
    ...raw,
    fromMe: normalizeFromMe(raw),
    messagesCredentialId: normalizeMessagesCredentialId(raw),
    revokedAt: normalizeRevokedAt(raw),
  };
}

export interface MessageFailedEvent {
  recipientPhone: string;
  clientReference: string | null;
  wamid: string;
  errorCode: number;
  errorTitle: string;
  errorDetails: string;
  isWindowExpired: boolean;
}

export interface ChatSendResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

function nestMessageToString(message: unknown): string | undefined {
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && message.every((x) => typeof x === 'string')) {
    return message.join(' ');
  }
  return undefined;
}

async function parseChatSendResponse(
  response: Response,
): Promise<ChatSendResult> {
  let raw: Record<string, unknown> = {};
  try {
    const text = await response.text();
    raw = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    return {
      success: false,
      error: 'No se pudo leer la respuesta del servidor.',
    };
  }
  if (!response.ok) {
    const msg =
      (typeof raw.error === 'string' && raw.error) ||
      nestMessageToString(raw.message) ||
      `Error del servidor (${response.status})`;
    return { success: false, error: msg };
  }
  if (raw.success === true) {
    return {
      success: true,
      messageId: typeof raw.messageId === 'string' ? raw.messageId : undefined,
    };
  }
  const err =
    (typeof raw.error === 'string' && raw.error) ||
    'No se pudo completar el envío.';
  return { success: false, error: err };
}

export interface WhatsappWebConnectionPayload {
  phase?: string;
  credential?: { status?: string } | null;
}

interface UseChatState {
  messages: Message[];
  isConnected: boolean;
  isLoading: boolean;
  failedEvent: MessageFailedEvent | null;
  conversationUpdateTrigger: number;
  activeCredentialId: number | null;
  messagesTotal: number;
  messagesPage: number;
  messagesLimit: number;
  webConnection: WhatsappWebConnectionPayload | null;
}

function encodeConversationPathSegment(clientReference: string): string {
  return encodeURIComponent(clientReference);
}

export const useChat = (
  clientReference: string | null,
  companyId: number,
  provider: ChatChannelProvider,
) => {
  const [state, setState] = useState<UseChatState>({
    messages: [],
    isConnected: false,
    isLoading: false,
    failedEvent: null,
    conversationUpdateTrigger: 0,
    activeCredentialId: null,
    messagesTotal: 0,
    messagesPage: 1,
    messagesLimit: 50,
    webConnection: null,
  });

  const socketRef = useRef<Socket | null>(null);

  const dismissFailedEvent = useCallback(() => {
    setState((prev) => ({ ...prev, failedEvent: null }));
  }, []);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      messages: [],
      failedEvent: null,
      activeCredentialId: null,
      messagesTotal: 0,
      messagesPage: 1,
      messagesLimit: 50,
    }));
  }, [clientReference, companyId, provider]);

  const connect = useCallback(() => {
    if (socketRef.current?.connected) return;

    const token = getStoredAccessToken();
    const socket = io(`${SOCKET_URL}/chat`, {
      auth: { token: token ?? '' },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    socket.on('connect', () => {
      setState((prev) => ({ ...prev, isConnected: true }));
      if (clientReference) {
        socket.emit('joinConversation', { clientReference });
      }
    });

    socket.on('disconnect', () => {
      setState((prev) => ({ ...prev, isConnected: false }));
    });

    socket.on('newMessage', (message: Message) => {
      setState((prev) => {
        const normalized = normalizeChatMessage(message);
        if (!credentialMatchesFilter(normalized, provider, prev.activeCredentialId)) {
          return prev;
        }
        return {
          ...prev,
          messages: [...prev.messages, normalized],
        };
      });
    });

    socket.on('messageEdited', (message: Message) => {
      setState((prev) => {
        const normalized = normalizeChatMessage(message);
        if (!credentialMatchesFilter(normalized, provider, prev.activeCredentialId)) {
          return prev;
        }
        return {
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.messageId === normalized.messageId
              ? { ...msg, ...normalized, body: normalized.body }
              : msg,
          ),
        };
      });
    });

    socket.on('messageRevoked', (message: Message) => {
      setState((prev) => {
        const normalized = normalizeChatMessage(message);
        if (!credentialMatchesFilter(normalized, provider, prev.activeCredentialId)) {
          return prev;
        }
        return {
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.messageId === normalized.messageId ? { ...msg, ...normalized } : msg,
          ),
        };
      });
    });

    socket.on('messageStatusUpdate', (data: { messageId: string; status: string }) => {
      const nextStatus = toMessageStatus(data.status);
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.messageId === data.messageId ? { ...msg, status: nextStatus } : msg,
        ),
      }));
    });

    socket.on('messageFailed', (event: MessageFailedEvent) => {
      if (provider !== 'meta') {
        return;
      }
      if (
        clientReference &&
        event.clientReference != null &&
        event.clientReference === clientReference
      ) {
        setState((prev) => ({
          ...prev,
          failedEvent: event,
          messages: prev.messages.map((msg) =>
            msg.messageId === event.wamid ? { ...msg, status: 'failed' as const } : msg,
          ),
        }));
      }
    });

    socket.on('conversationUpdate', () => {
      setState((prev) => ({
        ...prev,
        conversationUpdateTrigger: prev.conversationUpdateTrigger + 1,
      }));
    });
    socket.on('whatsappWebConnection', (payload: WhatsappWebConnectionPayload) => {
      setState((prev) => ({ ...prev, webConnection: payload }));
    });

    socketRef.current = socket;

    return () => {
      if (clientReference) {
        socket.emit('leaveConversation', { clientReference });
      }
      socket.disconnect();
    };
  }, [clientReference, provider]);

  useEffect(() => {
    const cleanup = connect();
    return () => {
      if (cleanup) cleanup();
    };
  }, [connect]);

  const loadMessages = useCallback(
    async (page: number = 1, limit: number = 50) => {
      if (!clientReference) return false;

      try {
        setState((prev) => ({ ...prev, isLoading: true }));
        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/messages?page=${page}&limit=${limit}&${qs}`,
          { headers: authHeadersJson() },
        );
        const data = await response.json();
        const rawList: Message[] = data.messages || [];
        const normalized = rawList.map((m) => normalizeChatMessage(m));
        const activeCredentialId =
          typeof data.messagesCredentialId === 'number' ? data.messagesCredentialId : null;
        const messagesTotal = typeof data.total === 'number' ? data.total : normalized.length;
        setState((prev) => {
          const nextMessages =
            page <= 1
              ? normalized
              : [
                  ...normalized.filter(
                    (incoming) =>
                      !prev.messages.some((existing) => existing.messageId === incoming.messageId),
                  ),
                  ...prev.messages,
                ];
          return {
            ...prev,
            messages: nextMessages,
            isLoading: false,
            activeCredentialId,
            messagesTotal,
            messagesPage: page,
            messagesLimit: limit,
          };
        });
        return true;
      } catch (error) {
        console.error('Error loading messages:', error);
        setState((prev) => ({ ...prev, isLoading: false }));
        return false;
      }
    },
    [clientReference, companyId, provider],
  );

  const sendText = useCallback(
    async (to: string, body: string): Promise<ChatSendResult> => {
      if (provider === 'whatsapp_web') {
        return { success: false, error: 'Esta conversación es solo lectura.' };
      }
      if (!clientReference) {
        return { success: false, error: 'No hay conversación seleccionada.' };
      }
      try {
        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/text?${qs}`,
          {
            method: 'POST',
            headers: authHeadersJson(),
            body: JSON.stringify({ to, body }),
          },
        );
        return parseChatSendResponse(response);
      } catch (error) {
        console.error('Error sending text:', error);
        return {
          success: false,
          error: 'No se pudo contactar al servidor. Revisá la conexión.',
        };
      }
    },
    [clientReference, companyId, provider],
  );

  const sendImage = useCallback(
    async (to: string, file: File, caption?: string): Promise<ChatSendResult> => {
      if (provider === 'whatsapp_web') {
        return { success: false, error: 'Esta conversación es solo lectura.' };
      }
      if (!clientReference) {
        return { success: false, error: 'No hay conversación seleccionada.' };
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('to', to);
        if (caption) formData.append('caption', caption);

        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/image?${qs}`,
          {
            method: 'POST',
            headers: authHeadersForMultipart(),
            body: formData,
          },
        );
        return parseChatSendResponse(response);
      } catch (error) {
        console.error('Error sending image:', error);
        return {
          success: false,
          error: 'No se pudo contactar al servidor. Revisá la conexión.',
        };
      }
    },
    [clientReference, companyId, provider],
  );

  const sendAudio = useCallback(
    async (to: string, blob: Blob, duration?: string): Promise<ChatSendResult> => {
      if (provider === 'whatsapp_web') {
        return { success: false, error: 'Esta conversación es solo lectura.' };
      }
      if (!clientReference) {
        return { success: false, error: 'No hay conversación seleccionada.' };
      }
      try {
        const formData = new FormData();
        formData.append('file', blob);
        formData.append('to', to);
        if (duration) formData.append('duration', duration);

        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/audio?${qs}`,
          {
            method: 'POST',
            headers: authHeadersForMultipart(),
            body: formData,
          },
        );
        return parseChatSendResponse(response);
      } catch (error) {
        console.error('Error sending audio:', error);
        return {
          success: false,
          error: 'No se pudo contactar al servidor. Revisá la conexión.',
        };
      }
    },
    [clientReference, companyId, provider],
  );

  const sendSticker = useCallback(
    async (to: string, file: File): Promise<ChatSendResult> => {
      if (provider === 'whatsapp_web') {
        return { success: false, error: 'Esta conversación es solo lectura.' };
      }
      if (!clientReference) {
        return { success: false, error: 'No hay conversación seleccionada.' };
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('to', to);

        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/sticker?${qs}`,
          {
            method: 'POST',
            headers: authHeadersForMultipart(),
            body: formData,
          },
        );
        return parseChatSendResponse(response);
      } catch (error) {
        console.error('Error sending sticker:', error);
        return {
          success: false,
          error: 'No se pudo contactar al servidor. Revisá la conexión.',
        };
      }
    },
    [clientReference, companyId, provider],
  );

  const sendLocation = useCallback(
    async (
      to: string,
      latitude: number,
      longitude: number,
      name?: string,
    ): Promise<ChatSendResult> => {
      if (provider === 'whatsapp_web') {
        return { success: false, error: 'Esta conversación es solo lectura.' };
      }
      if (!clientReference) {
        return { success: false, error: 'No hay conversación seleccionada.' };
      }
      try {
        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/location?${qs}`,
          {
            method: 'POST',
            headers: authHeadersJson(),
            body: JSON.stringify({ to, latitude, longitude, name }),
          },
        );
        return parseChatSendResponse(response);
      } catch (error) {
        console.error('Error sending location:', error);
        return {
          success: false,
          error: 'No se pudo contactar al servidor. Revisá la conexión.',
        };
      }
    },
    [clientReference, companyId, provider],
  );

  const sendTemplate = useCallback(
    async (
      to: string,
      templateName: string,
      languageCode: string,
    ): Promise<ChatSendResult> => {
      if (provider === 'whatsapp_web') {
        return { success: false, error: 'Esta conversación es solo lectura.' };
      }
      if (!clientReference) {
        return { success: false, error: 'No hay conversación seleccionada.' };
      }
      try {
        const qs = buildChatQuery(companyId, provider);
        const segment = encodeConversationPathSegment(clientReference);
        const response = await fetch(
          `${SOCKET_URL}/chat/conversations/${segment}/template?${qs}`,
          {
            method: 'POST',
            headers: authHeadersJson(),
            body: JSON.stringify({ to, templateName, languageCode }),
          },
        );
        const result = await parseChatSendResponse(response);
        if (result.success) {
          setState((prev) => ({ ...prev, failedEvent: null }));
        }
        return result;
      } catch (error) {
        console.error('Error sending template:', error);
        return {
          success: false,
          error: 'No se pudo contactar al servidor. Revisá la conexión.',
        };
      }
    },
    [clientReference, companyId, provider],
  );

  return {
    messages: state.messages,
    isConnected: state.isConnected,
    isLoading: state.isLoading,
    failedEvent: state.failedEvent,
    conversationUpdateTrigger: state.conversationUpdateTrigger,
    messagesTotal: state.messagesTotal,
    messagesPage: state.messagesPage,
    hasMoreMessages: state.messagesPage * state.messagesLimit < state.messagesTotal,
    webConnection: state.webConnection,
    loadMessages,
    sendText,
    sendImage,
    sendAudio,
    sendSticker,
    sendLocation,
    sendTemplate,
    dismissFailedEvent,
  };
};
